import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { createConnection, areConnected } from '../lib/supabase';
import { FiUserPlus, FiCheck, FiLoader } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import toast from 'react-hot-toast';

const ConnectButton = ({ targetProfile, targetWallet }) => {
  const { user, walletAddress, isAuthenticated } = useAuth();
  const { profile: currentProfile } = useProfile();
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    checkConnectionStatus();
  }, [currentProfile, targetProfile]);

  const checkConnectionStatus = async () => {
    if (!currentProfile || !targetProfile) {
      setChecking(false);
      return;
    }

    try {
      const { data } = await areConnected(currentProfile.id, targetProfile.id);
      setConnected(data.connected);
    } catch (error) {
      console.error('Error checking connection:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleConnect = async () => {
    if (!isAuthenticated || !currentProfile) {
      toast.error('Please connect your wallet to connect with others');
      return;
    }

    if (currentProfile.id === targetProfile.id) {
      toast.error('You cannot connect with yourself');
      return;
    }

    setConnecting(true);

    try {
      const { data, error } = await createConnection(
        currentProfile.id,
        targetProfile.id,
        walletAddress,
        targetWallet
      );

      if (error) {
        throw error;
      }

      setConnected(true);
      toast.success('Connected successfully!');
    } catch (error) {
      console.error('Error creating connection:', error);
      toast.error('Failed to connect. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  if (checking) {
    return (
      <button
        disabled
        className="glass text-white px-4 py-2 rounded-lg font-medium opacity-50 cursor-not-allowed inline-flex items-center"
      >
        <SafeIcon icon={FiLoader} className="mr-2 animate-spin" />
        Checking...
      </button>
    );
  }

  if (connected) {
    return (
      <button
        disabled
        className="glass-lime text-lime-400 px-4 py-2 rounded-lg font-medium border border-lime-500/50 inline-flex items-center"
      >
        <SafeIcon icon={FiCheck} className="mr-2" />
        Connected
      </button>
    );
  }

  if (!isAuthenticated || !currentProfile) {
    return (
      <button
        onClick={() => toast.error('Please connect your wallet first')}
        className="glass text-white px-4 py-2 rounded-lg font-medium hover:text-lime-400 transition-colors border border-dark-700 hover:border-lime-500/50 inline-flex items-center"
      >
        <SafeIcon icon={FiUserPlus} className="mr-2" />
        Connect
      </button>
    );
  }

  return (
    <button
      onClick={handleConnect}
      disabled={connecting}
      className="glass-lime text-lime-400 px-4 py-2 rounded-lg font-medium hover:text-lime-300 transition-colors border border-lime-500/50 hover:border-lime-400/70 shadow-glow-lime-sm hover:shadow-glow-lime disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
    >
      <SafeIcon icon={connecting ? FiLoader : FiUserPlus} className={`mr-2 ${connecting ? 'animate-spin' : ''}`} />
      {connecting ? 'Connecting...' : 'Connect'}
    </button>
  );
};

export default ConnectButton;

